import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { ReactComponent as AddIcon } from '../../assets/images/icons/add_16.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { ultraIsDiscoverable, useLocale } from '../../contexts/LocaleContext';
import useHardwareFirstCartUnits from '../../hooks/useHardwareFirstCartUnits';
import useHardwareFirstUpsellPricing from '../../hooks/useHardwareFirstUpsellPricing';
import { cartUpsell, hardwareFirstProductAdded } from '../../lib/analytics/events';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import { formatHardwarePrice, HARDWARE_FIRST_MAX_UNITS_PER_CART } from '../../lib/hardwareFirst';
import { cartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import { HARDWARE_FIRST_CART_ITEMS_ID, HARDWARE_FIRST_CHECKOUT_BAR_ID } from './HardwareFirstCartLayout';
import styles from './HardwareFirstUpsell.module.scss';

/**
 * How long the tile holds its confirmation. The added row renders and the totals settle, then the
 * viewport moves to the new row and the control returns to `add` so the same device can be added
 * again - the cap is five trackers, not one of each.
 */
const ADD_CONFIRMATION_MS = 1500;

type UpsellDeviceKey = 'mini' | 's3' | 'ultra';

interface UpsellDevice {
  key: UpsellDeviceKey;
  name: string;
  thumb: string;
  /** sku added straight to the cart; undefined when the device needs a variant choice first */
  addSku?: string;
  /** sku used only to price the tile when `addSku` is undefined */
  pricingSku?: string;
  /** where the tile sends the shopper when it cannot add straight to the cart */
  selectPath: string;
}

export interface UpsellTile {
  key: string;
  name: string;
  thumb: string;
  discount?: types.HardwareDiscountPreview;
  /** Transient confirmation after an add; the control returns to `add` when it clears. */
  justAdded: boolean;
  /** The tile routes to the size and color step instead of adding straight to the bag. */
  needsVariant: boolean;
}

interface HardwareFirstUpsellBlockProps {
  /** what adding one more tracker takes off the cart: two shares when it is the second tracker, one after */
  savingInCents: number;
  /** the flat per-tracker multi-device saving */
  perTrackerInCents: number;
  tiles: UpsellTile[];
  onAdd: (key: string) => void;
}

/**
 * Presentation for the cart cross-sell. Stacked rows with a glyph control on phones; once the
 * items column is wide enough the tiles sit three across and the control takes a label.
 */
export function HardwareFirstUpsellBlock({
  savingInCents,
  perTrackerInCents,
  tiles,
  onAdd,
}: HardwareFirstUpsellBlockProps) {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <section className={styles.block}>
      <h2 className={styles.title}>
        {t('hardware_first_upsell_title_amount', { amount: formatHardwarePrice(savingInCents, locale) })}
      </h2>
      <p className={styles.body}>
        {t('hardware_first_upsell_body_amount', {
          perTracker: formatHardwarePrice(perTrackerInCents, locale),
          max: HARDWARE_FIRST_MAX_UNITS_PER_CART,
        })}
      </p>
      <ul className={styles.tiles}>
        {tiles.map((tile) => (
          <li key={tile.key} className={styles.tile}>
            <img className={styles.thumb} src={tile.thumb} alt="" />
            <div className={styles.meta}>
              <span className={styles.name}>{tile.name}</span>
              {tile.discount && (
                <span className={styles.price}>
                  <span className={styles.discountedPrice}>
                    {formatHardwarePrice(tile.discount.discountedPriceInCents, locale)}
                  </span>
                  {tile.discount.listPriceInCents > tile.discount.discountedPriceInCents && (
                    <span className={styles.listPrice}>
                      {formatHardwarePrice(tile.discount.listPriceInCents, locale)}
                    </span>
                  )}
                </span>
              )}
            </div>
            <button
              className={classNames(styles.action, {
                [styles.actionAdded]: tile.justAdded,
              })}
              type="button"
              disabled={tile.justAdded}
              onClick={() => onAdd(tile.key)}
            >
              <span className={styles.actionIcon}>{tile.justAdded ? <CheckIcon /> : <AddIcon />}</span>
              <span className={styles.actionLabel}>
                {tile.justAdded
                  ? t('hardware_first_upsell_added')
                  : tile.needsVariant
                    ? t('hardware_first_select_size_color')
                    : t('hardware_first_upsell_add')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Gap left between the new row and whatever sits below it once the scroll settles. */
const NEW_ROW_CLEARANCE_PX = 16;

/**
 * Brings the newly added row into view. The row is appended to the item list, so parking the
 * list's bottom edge above the checkout bar puts it on screen. Measuring the bar covers both
 * layouts: it is fixed to the bottom of the viewport on mobile and `display: none` at desktop
 * widths, where it measures zero. A list already in place resolves to roughly the current offset.
 */
function scrollToNewCartItem() {
  const items = document.getElementById(HARDWARE_FIRST_CART_ITEMS_ID);
  if (!items) {
    return;
  }

  const bar = document.getElementById(HARDWARE_FIRST_CHECKOUT_BAR_ID);
  const barHeight = bar ? bar.getBoundingClientRect().height : 0;
  const bottom = items.getBoundingClientRect().bottom;
  const top = window.scrollY + bottom + barHeight + NEW_ROW_CLEARANCE_PX - window.innerHeight;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
}

/**
 * Cross-sell block on the hardware-first cart: what one more tracker saves. Mini and Ultra add in
 * place; series 3+ routes to the plp's size and color step, which owns the band the cart cannot
 * ask for.
 */
export default function HardwareFirstUpsell() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { country, currency } = useLocale();
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  const isUltraAvailable = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const unitsInCart = useHardwareFirstCartUnits();
  const [justAddedKey, setJustAddedKey] = useState<string | undefined>(undefined);
  const confirmTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(confirmTimer.current), []);

  const miniProduct = products.find((p) => p.id === types.miniCollarId) as types.IMiniProduct | undefined;
  const s3Product = products.find((p) => p.id === types.series3PlusCollarId) as types.ICollarKitProduct | undefined;
  const ultraProduct = products.find((p) => p.id === types.ultraCollarId) as types.IUltraProduct | undefined;

  const devices: UpsellDevice[] = [
    {
      key: 'mini',
      name: 'Fi Mini',
      thumb: '/product_images/smart-collar-mini.png',
      selectPath: AppPaths.Products('fi-mini') + '?type=dog',
      addSku: miniProduct?.sku,
    },
    {
      key: 's3',
      name: 'Fi Series 3+',
      thumb: '/product_images/smart-collar-v3b/gray.png',
      // the band size and color step on the pick-your-fi plp, which the cart has no room to ask for
      selectPath: `${AppPaths.Shop}?device=s3&step=size`,
      pricingSku: s3Product?.variants[0]?.sku,
    },
    {
      key: 'ultra',
      name: 'Fi Ultra',
      thumb: '/product_images/smart-collar-ultra.png',
      selectPath: AppPaths.Products('fi-ultra'),
      addSku: ultraProduct?.sku,
    },
  ];

  const miniDiscount = useHardwareFirstUpsellPricing(devices[0].addSku);
  const s3Discount = useHardwareFirstUpsellPricing(devices[1].pricingSku);
  const ultraDiscount = useHardwareFirstUpsellPricing(isUltraAvailable ? devices[2].addSku : undefined);
  const discountsByKey: Record<UpsellDeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: miniDiscount,
    s3: s3Discount,
    ultra: ultraDiscount,
  };

  const onAdd = useCallback(
    (key: string) => {
      const device = devices.find((candidate) => candidate.key === key);
      if (!device) {
        return;
      }

      if (!device.addSku) {
        history.push(device.selectPath);
        return;
      }

      dispatch(cartActions.addSingleLineCartItem({ sku: device.addSku, currency }));
      hardwareFirstProductAdded({
        sku: device.addSku,
        currency,
        source: 'cart_upsell',
        discount: discountsByKey[device.key],
      });
      setJustAddedKey(device.key);
      window.clearTimeout(confirmTimer.current);
      confirmTimer.current = window.setTimeout(() => {
        scrollToNewCartItem();
        setJustAddedKey(undefined);
      }, ADD_CONFIRMATION_MS);
    },
    // devices is rebuilt each render from redux products; the skus it closes over are what matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currency, dispatch, history, products],
  );

  const tiles: UpsellTile[] = devices
    .filter((device) => device.key !== 'ultra' || isUltraAvailable)
    .map((device) => ({
      key: device.key,
      name: device.name,
      thumb: device.thumb,
      discount: discountsByKey[device.key],
      justAdded: device.key === justAddedKey,
      needsVariant: !device.addSku,
    }));

  // the tiles are priced as the next unit in this cart, so their multi-device share is the per-tracker
  // saving. until pricing lands there is nothing to promise and the block stays out of the stack. the
  // second tracker unlocks the saving on the first one too, so the headline doubles it until the cart
  // holds two
  const perTrackerInCents = tiles.map((tile) => tile.discount).find((discount) => discount)?.multiDeviceDiscountInCents;
  const savingInCents = unitsInCart <= 1 ? (perTrackerInCents ?? 0) * 2 : (perTrackerInCents ?? 0);

  // one impression per mount, sent once the block has a saving to show
  const impressionSent = useRef(false);
  useEffect(() => {
    if (!perTrackerInCents || impressionSent.current) {
      return;
    }
    impressionSent.current = true;
    cartUpsell.viewed({ products: tiles.map((tile) => tile.name), savingInCents, trackerCount: unitsInCart });
    // tiles is rebuilt each render; the impression only needs the first priced render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perTrackerInCents]);

  if (!perTrackerInCents) {
    return null;
  }

  return (
    <HardwareFirstUpsellBlock
      savingInCents={savingInCents}
      perTrackerInCents={perTrackerInCents}
      tiles={tiles}
      onAdd={onAdd}
    />
  );
}
